import { PageSpecific } from '.generated/Feature.CW.model';
import { withStandardComponentWrapper } from 'helpers/HOC';
import { SvgIcon } from 'helpers/SvgIcon';
import { ComponentProps } from 'lib/component-props';
import React from 'react';
import { defaultConfig, tv } from 'tailwind-variants';
import MyChart from '../../../../helpers/MyChart/MyChart';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { useAppointmentModal } from 'helpers/Context/AppointmentModalProvider';
import { getItem } from 'lib/graphql/get-item';
import AppointmentModal from 'helpers/Context/AppointmentModal.graphql';
import useIsEditing from 'lib/hooks/useIsEditing';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import { useRouter } from 'next/router';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
defaultConfig.twMerge = false;

type VisitTypeProps = PageSpecific.OpenScheduling.VisitType & {
  id?: string;
};

type AnswerProps = PageSpecific.OpenScheduling.Answer & {
  id?: string;
  fields?: {
    answerLink?: QuestionProps | VisitTypeProps;
  };
};

type QuestionProps = PageSpecific.OpenScheduling.Question & {
  fields?: {
    answers?: AnswerProps[];
  };
};

type OpenSchedulingWizardProps = ComponentProps & {
  fields?: {
    startQuestion?: QuestionProps;
  };
};

export type TemplateProps = {
  item?: {
    epicDeptID?: Field<string> | null;
    epicPhysicianID?: Field<string> | null;
    name?: string;
    locationName?: Field<string> | null;
    providerTitle?: Field<string> | null;
  };
};

interface _KeyValuePair {
  key: string;
  value: string | number | object;
}

const OpenSchedulingWizard = React.memo(function OpenSchedulingWizard(
  props: OpenSchedulingWizardProps
) {
  const [currentQuestion, setCurrentQuestion] = React.useState<QuestionProps | null>(
    props?.fields?.startQuestion || null
  );
  const [isFirstQuestion, setIsFirstQuestion] = React.useState(true);
  const router = useRouter();
  const isEditing = useIsEditing();
  const [visitTypeID, setVisitTypeID] = React.useState<string | null>(null);
  const [epicID, setEpicID] = React.useState<string | null>(null);
  const [showIframe, setShowIframe] = React.useState(false);
  const [typeOfItem, setTypeOfItem] = React.useState<string | null>(null);
  const [keyValueArray, setKeyValueArray] = React.useState<_KeyValuePair[]>([]);
  // const { startQuestion } = props?.fields || {};
  const { base, heading, cardArrow } = TAILWIND_VARIANTS();

  const { itemId, setItemName } = useAppointmentModal();
  const { params } = props;

  React.useEffect(() => {
    let array = Object.entries(params || {}).map(([key, value]) => ({
      key,
      value,
    }));

    array = array.filter(
      (item) =>
        item.key !== 'GridParameters' &&
        item.key !== 'FieldNames' &&
        item.key !== 'DynamicPlaceholderId' &&
        item.key !== 'Styles' &&
        item.key !== 'styles'
    );

    setKeyValueArray(array);
  }, []);

  React.useEffect(() => {
    const query = itemId
      ? itemId
      : typeof router?.query?.itemId === 'string'
        ? router?.query?.itemId
        : '';
    if (!query) return;
    const fetchData = async () => {
      const data = (await getItem(AppointmentModal, query, 'en')) as TemplateProps;
      if (data?.item?.epicDeptID?.value) {
        setEpicID(data?.item?.epicDeptID?.value);
        setItemName(data?.item?.locationName?.value || '');
        setTypeOfItem('location');
      } else if (data?.item?.epicPhysicianID?.value) {
        setEpicID(data?.item?.epicPhysicianID?.value);
        setItemName(data?.item?.providerTitle?.value || '');
        setTypeOfItem('provider');
      } else {
        setEpicID(null);
        setItemName('');
        setTypeOfItem('location');
      }
    };
    fetchData();
  }, [itemId, router?.query, setItemName]);

  const handleStartOver = () => {
    setCurrentQuestion(props?.fields?.startQuestion || null);
    setShowIframe(false);
  };

  const handleAnswerClick = (answer: AnswerProps) => {
    const visitTypeID = answer?.fields?.answerLink?.fields?.visitTypeID as Field<string>;
    if (visitTypeID) {
      setShowIframe(true);
      setVisitTypeID(visitTypeID.value as string);
    } else {
      setShowIframe(false);
      setCurrentQuestion(answer?.fields?.answerLink as QuestionProps);
      setIsFirstQuestion(false);
    }
  };

  return (
    <section className={base()}>
      <div className="w-full mx-auto max-w-screen-xxl px-10 lg:px-20 py-24">
        {showIframe && !isEditing ? (
          <MyChart
            vt={visitTypeID || ''}
            epicID={epicID || ''}
            typeOfItem={typeOfItem || ''}
            additionalParams={keyValueArray}
          />
        ) : (
          <>
            <h3 className={heading()}>
              <RichTextWrapper field={currentQuestion?.fields?.questionText} />
            </h3>
            <p className="text-16 leading-24 mb-12 text-gray-900">
              <RichTextWrapper field={currentQuestion?.fields?.additionalInfo} />
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              {currentQuestion?.fields?.answers?.map((answer: AnswerProps, index: number) => (
                <button
                  key={`${answer?.id}-${index}`}
                  className={`group md:px-12 md:pb-12 p-12 bg-white rounded-md !w-full !h-auto shadow-default hover:shadow-hover flex justify-between items-left text-left ${answer.fields?.answerHelp?.value !== '' || isEditing ? 'flex-col gap-0' : 'flex-row gap-6'}`}
                  onClick={() => handleAnswerClick(answer)}
                >
                  <h4
                    className={`font-bold font-centuryGothic ${answer.fields?.answerHelp?.value !== '' ? 'mb-8 md:text-22 text-20 md:leading-31 leading-28' : 'text-22 leading-31'}`}
                  >
                    <RichTextWrapper field={answer.fields?.answerText} />
                  </h4>
                  <p className="text-16 leading-24 mb-6">
                    <RichTextWrapper field={answer.fields?.answerHelp} />
                  </p>

                  <div className="flex justify-end">
                    <span className={cardArrow()}>
                      <SvgIcon
                        icon="arrow-right"
                        size="sm"
                        className={`duration-700 ease-in-out absolute text-blue-base left-[30%] group-hover:left-[60%]`}
                      />
                      <SvgIcon
                        icon="arrow-right"
                        size="sm"
                        className={`duration-700 ease-in-out absolute text-white -left-[30%] group-hover:left-[30%]`}
                      />
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
        {!isFirstQuestion && !showIframe && (
          <ButtonWrapper
            text="Start Over"
            onClick={handleStartOver}
            className="w-full md:w-auto mt-[40px]"
            ctaVariant="secondary"
          />
        )}
      </div>
    </section>
  );
});

export const Default = withStandardComponentWrapper(OpenSchedulingWizard);

const TAILWIND_VARIANTS = tv({
  slots: {
    base: ['component bg-white w-full theme-primary'],
    heading: ['md:text-26 text-24 font-bold font-centuryGothic md:leading-30 leading-[29px] mb-16'],
    cardArrow:
      'md:w-[48px] md:h-[48px] w-[40px] h-[40px] rounded-full border-2 text-center border-blue-base flex items-center justify-center transition-all duration-300 ease-in-out overflow-hidden duration-700 relative group-hover:bg-blue-base',
  },
});
